import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import api from "./utils/axiosConfig";
import { getApiErrorCode } from "./utils/apiError";
import { LoginParams, postLogin, LoginApiResponse, logoutApi } from "./api";
/*
 * Imported from the module rather than the barrel, deliberately.
 *
 * Several suites mock `"./api"` wholesale with a factory that spreads only the
 * parts they care about. Reaching for `canPlaceBets` through the barrel made it
 * `undefined` in every one of them, and this provider wraps every page — so a
 * change about betting broke the dashboard and the reveal suites, which have
 * nothing to do with it. The specific path is not mocked, and there is no
 * reason for the neighbour to go through the front door anyway.
 */
import { canPlaceBets, type PlayerStatus } from "./api/getProfile";
import { ME_QUERY_KEY, useProfileQuery } from "./hooks/useProfile";

interface User {
  user_id: string;
  name: string;
  phone_no: string;
  /** Only `active` may bet — see `canPlaceBets`. */
  status: PlayerStatus;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  /**
   * Whether this account may place a bet.
   *
   * Exposed here rather than left to each screen to derive from `user.status`,
   * so there is one answer and no screen can disagree with the API by getting
   * the comparison subtly wrong. It is the polite half of the rule — the API
   * refuses a non-active account regardless — but a form that lets somebody
   * fill it in and then rejects it wasted their time.
   */
  canBet: boolean;
  login: (userCredentials: LoginParams) => Promise<LoginApiResponse>;
  logout: () => Promise<void>;
  /** Drops the token locally, for when the server has already ended the session. */
  endSession: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: true,
  canBet: false,
  login: async () => {
    return {
      status: 0,
      system: "error",
      message: "Login function not implemented",
      data: {
        user_id: "",
        name: "",
        phone_no: "",
        token: "",
      },
    };
  },
  logout: async () => {},
  endSession: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token")
  );

  // Set before the first request rather than in an effect: the profile query
  // below fires on the same render, and an effect would run after it.
  if (token) api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

  /* The session check and the profile are the same `/me` call. Asking for it
     through the shared query means the shell's balance and this provider's
     identity ride on one request, polled once for both. */
  const { data, isLoading, isError, error } = useProfileQuery(Boolean(token));

  const clearSession = useMemo(
    () => () => {
      setToken(null);
      localStorage.removeItem("token");
      delete api.defaults.headers.common["Authorization"];
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
    },
    [queryClient]
  );

  // A rejected /me is an ended session — the same outcome the old interval
  // produced, reached through the query's error state instead.
  useEffect(() => {
    if (!isError) return;

    /*
     * Say *why* when the server named a cause worth naming.
     *
     * One account holds one session, so signing in on another device ends this
     * one. Without this the player is simply returned to the login screen, which
     * reads as a bug — and if it was not them who signed in, the silence wastes
     * the only moment the app could have warned them.
     */
    if (getApiErrorCode(error) === "SESSION_SUPERSEDED") {
      toast.info(t("session_signed_in_elsewhere"));
    }
    clearSession();
  }, [isError, error, clearSession, t]);

  /* Rebuilt only when the fields actually change. React Query hands back the
     previous object when a poll returns identical data, so this memo holds its
     identity and consumers keyed on `user` do not re-fetch every poll. */
  const user = useMemo<User | null>(() => {
    if (!token || !data?.data) return null;
    return {
      user_id: data.data.user_id,
      name: data.data.name,
      phone_no: data.data.phone_no,
      status: data.data.status,
    };
  }, [token, data?.data]);

  const login = async (
    userCredentials: LoginParams
  ): Promise<LoginApiResponse> => {
    const response = await postLogin(userCredentials);

    if (response.status === 1 && response.data) {
      setToken(response.data.token);
      localStorage.setItem("token", response.data.token);

      api.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${response.data.token}`;

      // The new session's own /me, rather than the previous account's.
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    }

    return response;
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch (err) {
      console.error("Logout API error:", err);
    }

    clearSession();
  };

  const value = useMemo(
    () => ({
      user,
      token,
      loading: Boolean(token) && isLoading,
      // False while the profile is still loading, so a screen never opens in
      // the permissive state and then closes — better to start shut and open.
      canBet: canPlaceBets(user?.status),
      login,
      logout,
      endSession: clearSession,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, token, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
